import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, AlertCircle, Loader2, Calendar, MapPin, Truck, User, FileText, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  'New': 'bg-blue-500',
  'Open': 'bg-blue-500',
  'In Process': 'bg-amber-500',
  'Closed': 'bg-emerald-500',
  'Resolved': 'bg-emerald-500',
};

const LOSS_LABELS = { al: 'Auto Liability', pd: 'Physical Damage', cd: 'Cargo Damage', ocac: 'Occupational Accident', wc: 'Workers Compensation' };
const SUBMITTAL_COLORS = {
  'Submitted': 'bg-blue-100 text-blue-700',
  'Settled': 'bg-emerald-100 text-emerald-700',
  'Pending': 'bg-gray-100 text-gray-600',
};

function formatDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return s; }
}

function formatDateTime(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return s; }
}

export default function CustomerPortal() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/portal/claim`, { params: { token } });
        if (mounted) { setData(res.data); setLoading(false); }
      } catch (err) {
        if (!mounted) return;
        const code = err.response?.status;
        let msg = err.response?.data?.detail || 'Could not load this claim.';
        if (code === 410) msg = 'This link has expired. Please ask the claims team to send you a fresh update email.';
        else if (code === 401) msg = 'This link is invalid or has been tampered with.';
        else if (code === 404) msg = 'This claim no longer exists or has been deleted.';
        else if (code === 403) msg = 'The claims team has disabled the customer portal for this company.';
        setError(msg);
        setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="portal-loading">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Loader2 className="w-7 h-7 animate-spin" />
          <p className="text-sm">Loading claim details…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="portal-error">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 shadow-sm text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Unable to open this claim</h1>
          <p className="text-sm text-gray-600 leading-relaxed">{error}</p>
          <p className="mt-6 text-xs text-gray-400">If you believe this is a mistake, contact your claims team directly.</p>
        </div>
      </div>
    );
  }

  const { claim, history, branding } = data;
  const brandColor = branding?.org_color || branding?.tenant_color || '#14b8a6';
  const senderName = branding?.org_name ? `${branding.org_name} · ${branding.tenant_name}` : (branding?.tenant_name || 'All About Claims');
  const statusColor = STATUS_COLORS[claim.status] || 'bg-gray-500';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="portal-page">
      {/* Header bar */}
      <div className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: brandColor }}>
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 leading-tight">
                {branding?.tenant_name || 'Claims Portal'}
              </p>
              {branding?.org_name && branding.org_name !== claim.company_name && (
                <p className="text-[11px] text-gray-400">via {branding.org_name}</p>
              )}
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Customer View</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-5">
        {/* Status hero */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">Claim Update</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 truncate" data-testid="portal-company-name">{claim.company_name}</h1>
              <p className="text-xs text-gray-500 font-mono" data-testid="portal-report-no">{claim.report_no}</p>
            </div>
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold text-white ${statusColor} shrink-0`} data-testid="portal-status">
              {claim.status || '—'}
            </span>
          </div>

          {/* Fact grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4 border-t border-gray-100">
            <Fact icon={Calendar} label="Date of Loss" value={formatDate(claim.date_of_loss)} testid="portal-fact-date" />
            <Fact icon={MapPin}   label="Location"     value={claim.location || '—'} testid="portal-fact-location" />
            {claim.drivers_name && <Fact icon={User}    label="Driver"   value={claim.drivers_name} testid="portal-fact-driver" />}
            {(claim.truck_no || claim.trailer_no) && (
              <Fact icon={Truck} label="Truck / Trailer" value={[claim.truck_no, claim.trailer_no].filter(Boolean).join(' / ') || '—'} testid="portal-fact-vehicle" />
            )}
            {claim.fault && <Fact icon={FileText} label="Fault" value={claim.fault} testid="portal-fact-fault" />}
            <Fact icon={Clock} label="Last Updated" value={formatDateTime(claim.last_updated)} testid="portal-fact-updated" />
          </div>

          {claim.description && (
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Incident Description</p>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line" data-testid="portal-description">{claim.description}</p>
            </div>
          )}
        </div>

        {/* Loss types */}
        {claim.loss_types?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-testid="portal-loss-types">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">Coverage Lines</p>
            <div className="space-y-2">
              {claim.loss_types.map((lt) => (
                <div key={lt.key} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg" data-testid={`portal-loss-${lt.key}`}>
                  <span className="text-sm font-medium text-gray-800">{LOSS_LABELS[lt.key] || lt.key.toUpperCase()}</span>
                  {lt.submittal && (
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${SUBMITTAL_COLORS[lt.submittal] || SUBMITTAL_COLORS.Pending}`}>
                      {lt.submittal}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        {history?.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-testid="portal-history">
            <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-3">Recent Updates</p>
            <ol className="space-y-3 border-l-2 border-gray-100 pl-4 ml-1">
              {history.map((h, idx) => (
                <li key={idx} className="relative" data-testid={`portal-history-${idx}`}>
                  <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full border-2 border-white" style={{ background: brandColor }} />
                  <p className="text-sm font-medium text-gray-900">{h.subject || h.event}</p>
                  {h.message && <p className="text-sm text-gray-600 mt-0.5 whitespace-pre-line">{h.message}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{formatDateTime(h.sent_at)}{h.triggered_by_name ? ` · by ${h.triggered_by_name}` : ''}</p>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 pt-4 pb-8 leading-relaxed">
          <p>This is a secure read-only view. To respond or upload documents, please reply to your latest claim email.</p>
          <p className="mt-1">Sent by {senderName}</p>
        </div>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-2" data-testid={testid}>
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
        <p className="text-sm text-gray-800 break-words">{value}</p>
      </div>
    </div>
  );
}
